"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, Copy, Check, Mail, AlertTriangle } from "lucide-react"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { ROLE_HIERARCHY } from "@/lib/auth/permissions.utils"
import type { Role } from "@/lib/auth/permissions.types"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





interface SendingEmailInfo {
  email: string | null
  isDefault: boolean
  configured: boolean
  isPlatformFallback: boolean
}

interface InvitedUser {
  email: string
  name: string
  role: string
}

interface InviteUserFormProps {
  onSuccess?: () => void
}

export function InviteUserForm({ onSuccess }: InviteUserFormProps = {}) {


  const tUi = useTranslations("Admin.AdminInviteUserForm")

  const { fetchWithAuth, user } = useAuth()
  const { isSuperUser } = usePermissions()
  const currentUserLevel = user?.role ? (ROLE_HIERARCHY[user.role as Role] ?? 99) : 99
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ user: InvitedUser; inviteUrl: string; emailSent: boolean; sendingEmail: string | null } | null>(null)
  const [copied, setCopied] = useState(false)
  const [sendingEmailInfo, setSendingEmailInfo] = useState<SendingEmailInfo | null>(null)
  const [loadingSendingEmail, setLoadingSendingEmail] = useState(true)

  useEffect(() => {
    async function fetchSendingEmail() {
      try {
        const response = await fetchWithAuth("/api/auth/google/sending-email")
        if (response.ok) {
          const data = await response.json()
          setSendingEmailInfo(data)
        }
      } catch (err) {
        console.error("Failed to fetch sending email:", err)
      } finally {
        setLoadingSendingEmail(false)
      }
    }
    fetchSendingEmail()
  }, [fetchWithAuth])

  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role: "viewer" as Role,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await fetchWithAuth("/api/users/invite", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "Failed to invite user")
      }

      setSuccess({
        user: data.user,
        inviteUrl: data.invite_url,
        emailSent: data.email_sent,
        sendingEmail: data.sending_email,
      })

      // Reset form
      setFormData({
        email: "",
        name: "",
        role: "viewer",
      })

      // Don't auto-redirect, let user see the invite URL and decide when to close
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToInviteUser"))
    } finally {
      setLoading(false)
    }
  }

  const copyInviteUrl = () => {
    if (success?.inviteUrl) {
      navigator.clipboard.writeText(success.inviteUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle><span key={tUi("inviteNewUser")}>{tUi("inviteNewUser")}</span></CardTitle>
        <CardDescription><span key={tUi("sendAnInvitationToJoinYourOrganization")}>{tUi("sendAnInvitationToJoinYourOrganization")}</span></CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email"><span key={tUi("emailAddress")}>{tUi("emailAddress")}</span></Label>
            <Input
              id="email"
              type="email"
              placeholder={"user@example.com"}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={loading}
            />
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name"><span key={tUi("fullName")}>{tUi("fullName")}</span></Label>
            <Input
              id="name"
              type="text"
              placeholder={"John Doe"}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              disabled={loading}
            />
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role"><span key={tUi("role")}>{tUi("role")}</span></Label>
            <Select
              value={formData.role}
              onValueChange={(value) => setFormData({ ...formData, role: value as Role })}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {isSuperUser && <SelectItem value="super_user"><span key={tUi("superUser")}>{tUi("superUser")}</span></SelectItem>}
                {/* integrator is platform-staff: only super_user can assign it (D1) */}
                {isSuperUser && <SelectItem value="integrator"><span key={tUi("integrator")}>{tUi("integrator")}</span></SelectItem>}
                {currentUserLevel <= ROLE_HIERARCHY.admin && <SelectItem value="admin"><span key={tUi("admin")}>{tUi("admin")}</span></SelectItem>}
                {currentUserLevel <= ROLE_HIERARCHY.manager && <SelectItem value="manager"><span key={tUi("manager")}>{tUi("manager")}</span></SelectItem>}
                <SelectItem value="viewer"><span key={tUi("viewer")}>{tUi("viewer")}</span></SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sending Email Info */}
          <div className="rounded-md border p-3 bg-muted/50">
            <div className="flex items-start gap-2">
              {loadingSendingEmail ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span><span key={tUi("loadingSendingEmail")}>{tUi("loadingSendingEmail")}</span></span>
                </div>
              ) : sendingEmailInfo?.configured ? (
                <>
                  <Mail className="h-4 w-4 text-green-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-muted-foreground">
                      <span><span key={tUi("invitationWillBeSentFrom")}>{tUi("invitationWillBeSentFrom")}</span></span>{" "}
                      <span className="font-medium text-foreground">{sendingEmailInfo.email}</span>
                      {sendingEmailInfo.isPlatformFallback ? (
                        <span className="ml-1 text-xs text-yellow-600"><span key={tUi("platformDefault")}>{tUi("platformDefault")}</span></span>
                      ) : (
                        sendingEmailInfo.isDefault && (
                          <span className="ml-1 text-xs text-yellow-600"><span key={tUi("default")}>{tUi("default")}</span></span>
                        )
                      )}
                    </p>
                    {sendingEmailInfo.isPlatformFallback && (
                      <p className="text-xs text-muted-foreground mt-1">
                        <span key={tUi("connectYourOwnEmailInIntegrationsGoogleTo")}>{tUi("connectYourOwnEmailInIntegrationsGoogleTo")}</span></p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-yellow-700">
                      <span key={tUi("noSendingEmailConfiguredTheInvitationLinkWill")}>{tUi("noSendingEmailConfiguredTheInvitationLinkWill")}</span></p>
                    <p className="text-xs text-muted-foreground mt-1">
                      <span key={tUi("configureADefaultEmailInIntegrationsGoogleTo")}>{tUi("configureADefaultEmailInIntegrationsGoogleTo")}</span></p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert>
              <AlertDescription>
                <div className="space-y-3">
                  <p className="font-semibold"><span key={tUi("userInvitedSuccessfully")}>{tUi("userInvitedSuccessfully")}</span></p>
                  <div className="space-y-1">
                    <p>
                      <strong><span key={tUi("email")}>{tUi("email")}</span></strong> {success.user.email}
                    </p>
                    <p>
                      <strong><span key={tUi("name")}>{tUi("name")}</span></strong> {success.user.name}
                    </p>
                    <p>
                      <strong><span key={tUi("role2")}>{tUi("role2")}</span></strong> {success.user.role}
                    </p>
                  </div>

                  {/* Email sending status */}
                  {success.emailSent ? (
                    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded">
                      <Mail className="h-4 w-4" />
                      <span><TranslationText>{tUi.rich("invitationEmailSentFromTag0Value0Tag0", { value0: String(success.sendingEmail), tag0: chunks => (<strong>{chunks}</strong>) })}</TranslationText></span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 text-sm text-yellow-700 bg-yellow-50 p-2 rounded">
                      <AlertTriangle className="h-4 w-4 mt-0.5" />
                      <span>
                        <span key={tUi("emailWasNotSentAutomaticallyPleaseShareThe")}>{tUi("emailWasNotSentAutomaticallyPleaseShareThe")}</span></span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-sm font-medium"><span key={tUi("invitationLink")}>{tUi("invitationLink")}</span></Label>
                    <div className="flex gap-2">
                      <Input
                        value={success.inviteUrl}
                        readOnly
                        className="font-mono text-xs"
                        onClick={(e) => e.currentTarget.select()}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={copyInviteUrl}>
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      <span key={tUi("shareThisLinkSecurelyWithTheUserIt")}>{tUi("shareThisLinkSecurelyWithTheUserIt")}</span></p>
                  </div>
                  {/* Action buttons after success */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSuccess(null)
                        setError(null)
                      }}
                      className="flex-1"
                    >
                      <span key={tUi("inviteAnotherUser")}>{tUi("inviteAnotherUser")}</span></Button>
                    {onSuccess && (
                      <Button type="button" onClick={onSuccess} className="flex-1">
                        <span key={tUi("backToUserManagement")}>{tUi("backToUserManagement")}</span></Button>
                    )}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Submit Button - hide when success is shown */}
          {!success && (
            <Button type="submit" disabled={loading} className="w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <span><span key={tUi("sendInvitation")}>{tUi("sendInvitation")}</span></span>
            </Button>
          )}
        </form>
      </CardContent>
    </Card>
  )
}
